import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import roleService from "../services/roleService";
import { toast } from "react-toastify";

// Async thunks for role operations
export const createRole = createAsyncThunk(
  "role/createRole",
  async (roleData, { rejectWithValue }) => {
    try {
      const response = await roleService.createRole(roleData);
      toast.success(response.message || "Role created successfully");
      return response.data;
    } catch (error) {
      toast.error(error.message || "Failed to create role");
      return rejectWithValue(error.message || "Failed to create role");
    }
  }
);

export const getAllRoles = createAsyncThunk(
  "role/getAllRoles",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await roleService.getAllRoles(params);
      return {
        roles: response.data,
        pagination: response.pagination,
      };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch roles");
    }
  }
);

export const getRoleById = createAsyncThunk(
  "role/getRoleById",
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await roleService.getRoleById(roleId);
      return response.data;
    } catch (error) {
      toast.error(error.message || "Failed to fetch role");
      return rejectWithValue(error.message || "Failed to fetch role");
    }
  }
);

export const updateRole = createAsyncThunk(
  "role/updateRole",
  async ({ roleId, roleData }, { rejectWithValue }) => {
    try {
      const response = await roleService.updateRole(roleId, roleData);
      toast.success(response.message || "Role updated successfully");
      return response.data;
    } catch (error) {
      toast.error(error.message || "Failed to update role");
      return rejectWithValue(error.message || "Failed to update role");
    }
  }
);

export const deleteRole = createAsyncThunk(
  "role/deleteRole",
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await roleService.deleteRole(roleId);
      toast.success(response.message || "Role deleted successfully");
      return roleId;
    } catch (error) {
      toast.error(error.message || "Failed to delete role");
      return rejectWithValue(error.message || "Failed to delete role");
    }
  }
);

export const getActiveRoles = createAsyncThunk(
  "role/getActiveRoles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await roleService.getActiveRoles();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch active roles");
    }
  }
);

// Initial state
const initialState = {
  roles: [],
  currentRole: null,
  activeRoles: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalRoles: 0,
  },
  loading: false,
  error: null,
  createLoading: false,
  createSuccess: false,
  updateLoading: false,
  updateSuccess: false,
  activeRolesLoading: false,
};

// Role slice
const roleSlice = createSlice({
  name: "role",
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
    },
    clearCurrentRole: (state) => {
      state.currentRole = null;
    },
    resetCreate: (state) => {
      state.createSuccess = false;
    },
    resetUpdate: (state) => {
      state.updateSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Role
      .addCase(createRole.pending, (state) => {
        state.createLoading = true;
        state.createSuccess = false;
        state.error = null;
      })
      .addCase(createRole.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createSuccess = true;
        state.roles = [action.payload, ...state.roles];
        state.error = null;
      })
      .addCase(createRole.rejected, (state, action) => {
        state.createLoading = false;
        state.createSuccess = false;
        state.error = action.payload;
      })
      // Get All Roles
      .addCase(getAllRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.roles = action.payload.roles;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getAllRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Role By ID
      .addCase(getRoleById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRoleById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentRole = action.payload;
        state.error = null;
      })
      .addCase(getRoleById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Role
      .addCase(updateRole.pending, (state) => {
        state.updateLoading = true;
        state.updateSuccess = false;
        state.error = null;
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = true;
        state.currentRole = action.payload;
        state.roles = state.roles.map((role) =>
          role._id === action.payload._id ? action.payload : role
        );
        state.error = null;
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = false;
        state.error = action.payload;
      })
      // Delete Role
      .addCase(deleteRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.loading = false;
        state.roles = state.roles.filter((role) => role._id !== action.payload);
        state.currentRole = null;
        state.error = null;
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Active Roles
      .addCase(getActiveRoles.pending, (state) => {
        state.activeRolesLoading = true;
        state.error = null;
      })
      .addCase(getActiveRoles.fulfilled, (state, action) => {
        state.activeRolesLoading = false;
        state.activeRoles = action.payload;
        state.error = null;
      })
      .addCase(getActiveRoles.rejected, (state, action) => {
        state.activeRolesLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearErrors, clearCurrentRole, resetCreate, resetUpdate } =
  roleSlice.actions;
export default roleSlice.reducer;

